/**
 * @fileoverview Constants and dispatch utilities for audit event notifications.
 * @module audit/events
 *
 * Responsibilities:
 * - Define the custom event name for audit log write notifications
 * - Define the event detail shape (collection + documentId)
 * - Dispatch CustomEvent on window when an audit entry is written
 *
 * Dependencies:
 * - ./collections (resolveAuditCollection)
 *
 * Architecture:
 * - Browser-only event dispatch. No-op in non-browser environments.
 * - Used to notify AuditLogTimeline to re-read from Dexie after a write.
 *
 * Notes:
 * - Event name: 'fs:audit-log-written'
 * - Collection is resolved to canonical form before dispatch
 */

import { resolveAuditCollection } from './collections';

export const AUDIT_LOG_WRITTEN_EVENT = 'fs:audit-log-written';

/**
 * @contract AuditLogWrittenDetail
 *
 * Purpose: Payload shape for the audit-log-written custom event.
 * Fields: collection (canonical audit collection ID), documentId (string).
 * Rules: collection should already be resolved via resolveAuditCollection.
 * Sync: N/A — ephemeral browser event payload.
 */
export interface AuditLogWrittenDetail {
  collection: string;
  documentId: string;
}

/**
 * @process dispatchAuditLogWritten
 *
 * Steps:
 * 1. Check if running in a browser environment.
 * 2. Resolve collection to canonical form.
 * 3. Dispatch a CustomEvent on window with the detail.
 *
 * Inputs:
 * - detail: AuditLogWrittenDetail — collection + documentId.
 *
 * Outputs:
 * - void — fire-and-forget event dispatch.
 *
 * Failure Handling:
 * - No-op if window is undefined (SSR / Node.js environments).
 *
 * Idempotency: NOT REQUIRED — each call dispatches a new event.
 */
export function dispatchAuditLogWritten(detail: AuditLogWrittenDetail): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(AUDIT_LOG_WRITTEN_EVENT, {
    detail: {
      collection: resolveAuditCollection(detail.collection),
      documentId: String(detail.documentId),
    },
  }));
}
